"use client";
// @ts-strict-ignore
import * as React from "react";
import NextLink from "components/parts/NextLink";
import Button from "components/parts/button";
import { Footer, FooterContainer, MainText, SubTitle } from "./style";
import { ThemeProvider } from "styled-components";
import darkTheme from "themes/dark";
import { useTranslations } from "i18n";
import { FILES } from "i18n/constants";
import { useCountryStateCookies } from "hooks/cookies/useCountryStateCookies";
import { Flex, HubbleProvider, Text } from "@hubble/web";
import Badges from "../../common/badges";

interface PropsButton {
  isLight?: boolean;
  isDarkTheme?: boolean;
  isPrimary?: boolean;
  isBlue?: boolean;
  isCryptopedia?: boolean;
  isSmall?: boolean;
  isWhite?: boolean;
}

interface Props {
  ctaTitle?: string;
  ctaParagraph?: string | React.ReactNode;
  ctaButtonText?: string;
  ctaLink?: string;
  innerRef?: any;
  id?: string;
  background?: string;
  noCtaLink?: boolean;
  buttonProps?: PropsButton;
  hasUKDisclaimer?: boolean;
  showAwardsPanel?: boolean;
}

const getContent = (
  ctaTitle: string,
  defaultTitle: string,
  isUK: boolean,
  hasUKDisclaimer: boolean,
  showAwardsPanel: boolean
) => {
  let title = "";
  let disclaimer = "";
  let hasAwardsPanel = false;
  if (ctaTitle) {
    // components can pass custom titles, only add asterisk if passing hasUKDisclaimer
    title = ctaTitle;
    disclaimer =
      isUK && hasUKDisclaimer ? "ISO 27001 and SOC 2 Type 2 certified" : "";
    // using showAwardsPanel to determine if we should show the awards panel because we don't want to show it on pages like the careers page even if it's a UK user
    hasAwardsPanel = showAwardsPanel;
  } else {
    // add logic for the default uk text "Award winning"
    title = defaultTitle;
    disclaimer = isUK ? "See award list in the section below" : "";
    hasAwardsPanel = isUK;
  }

  return { title, disclaimer, hasAwardsPanel };
};
/**
 * PdpFooter (ProDuctPage footer) is a common layout used at the end of product pages that includes a link to the register an account on Gemini, or another major call to action.
 */
const PdpFooter = (props: Props) => {
  const {
    ctaButtonText,
    ctaTitle,
    ctaParagraph,
    ctaLink,
    innerRef,
    background,
    noCtaLink,
    hasUKDisclaimer = false,
    showAwardsPanel = false,
    buttonProps = { isBlue: true }
  } = props;
  const t = useTranslations(FILES.COMMON);
  const { isUK } = useCountryStateCookies();

  const defaultTitle = isUK ? t("pdpFooterTitleUK") : t("pdpFooterTitle");

  const { title, disclaimer, hasAwardsPanel } = getContent(
    ctaTitle,
    defaultTitle,
    isUK,
    hasUKDisclaimer,
    showAwardsPanel
  );
  return (
    <React.Fragment>
      <HubbleProvider scheme="dark">
        <ThemeProvider theme={darkTheme}>
          <Footer
            backgroundColor={background || "secondary"}
            ref={innerRef || undefined}
          >
            <FooterContainer>
              <MainText
                textAlign={"center"}
                oneLine={!ctaParagraph}
                noMargin={noCtaLink}
              >
                {title}
              </MainText>
              {Boolean(disclaimer) && (
                <Flex justifyContent="center">
                  <Text.Body>
                    <sup>*</sup>
                    {disclaimer}
                  </Text.Body>
                </Flex>
              )}
              {ctaParagraph ? (
                <SubTitle textAlign={"center"}>{ctaParagraph}</SubTitle>
              ) : null}
              {!noCtaLink && (
                <Flex justifyContent="center">
                  <NextLink
                    id="product-footer-btn"
                    href={ctaLink || "https://exchange.gemini.com/register"}
                  >
                    <Button
                      {...buttonProps}
                      style={{ display: "inline-block" }}
                    >
                      {ctaButtonText || t("getStarted")}
                    </Button>
                  </NextLink>
                </Flex>
              )}
            </FooterContainer>
          </Footer>
        </ThemeProvider>
      </HubbleProvider>
      {hasAwardsPanel && <Badges />}
    </React.Fragment>
  );
};

export default PdpFooter;
